import { fetchJSON as fetchAPI } from './client';

const BASE = import.meta.env.VITE_DOCUMENTS_API ?? 'http://localhost:8165';

export type DocumentDashboard = {
  tenant_id: string;
  total_documents: number;
  active_documents: number;
  archived_documents: number;
  trashed_documents: number;
  total_folders: number;
  ocr_processed_24h: number;
  extractions_24h: number;
  pending_signatures: number;
  completed_signatures: number;
  retention_policies: number;
  legal_holds: number;
  category_breakdown: Record<string, number>;
  recent_activity?: ActivityEntry[];
  generated_at: string;
};

export type ActivityEntry = {
  id: string;
  tenant_id: string;
  category: string;
  action: string;
  message: string;
  actor?: string;
  created_at: string;
};

export type Document = {
  id: string;
  tenant_id: string;
  name: string;
  category: string;
  status: string;
  folder_id?: string;
  mime_type?: string;
  size_bytes: number;
  current_version: number;
  tags?: string[];
  metadata?: Record<string, unknown>;
  module?: string;
  reference_id?: string;
  ai_generated: boolean;
  locked: boolean;
  ocr_processed: boolean;
  extracted: boolean;
  signed: boolean;
  created_at: string;
  updated_at: string;
};

export type Folder = {
  id: string;
  tenant_id: string;
  parent_id?: string;
  name: string;
  path: string;
  virtual: boolean;
  created_at: string;
};

export type OCRResult = {
  id: string;
  document_id: string;
  text: string;
  confidence: number;
  language?: string;
  template_type?: string;
  fields?: Record<string, unknown>;
  validated: boolean;
  processing_ms: number;
  created_at: string;
};

export type AIExtraction = {
  id: string;
  document_id: string;
  extraction_type: string;
  summary?: string;
  entities?: Record<string, unknown>;
  classification?: string;
  tags?: string[];
  risk_score?: number;
  confidence: number;
  created_at: string;
};

export type SignatureRecord = {
  id: string;
  document_id: string;
  signer_name: string;
  signer_email: string;
  status: string;
  signature_type: string;
  signed_at?: string;
  verified: boolean;
  certificate_id?: string;
};

export type CreateDocumentRequest = {
  name: string;
  category?: string;
  folder_id?: string;
  mime_type?: string;
  content?: string;
  tags?: string[];
  metadata?: Record<string, unknown>;
  module?: string;
  reference_id?: string;
  ai_generated?: boolean;
};

export type SearchRequest = {
  q?: string;
  category?: string;
  folder_id?: string;
  module?: string;
  status?: string;
  semantic?: boolean;
  limit?: number;
};

export const documentsApi = {
  dashboard: () => fetchAPI<DocumentDashboard>(`${BASE}/api/v1/documents/dashboard`),
  listDocuments: (limit = 50) => fetchAPI<{ documents: Document[] }>(`${BASE}/api/v1/documents?limit=${limit}`),
  getDocument: (id: string) => fetchAPI<Document>(`${BASE}/api/v1/documents/${id}`),
  createDocument: (req: CreateDocumentRequest) =>
    fetchAPI<Document>(`${BASE}/api/v1/documents`, { method: 'POST', body: JSON.stringify(req) }),
  updateDocument: (id: string, req: Partial<CreateDocumentRequest>) =>
    fetchAPI<Document>(`${BASE}/api/v1/documents/${id}`, { method: 'PUT', body: JSON.stringify(req) }),
  deleteDocument: (id: string) =>
    fetchAPI<void>(`${BASE}/api/v1/documents/${id}`, { method: 'DELETE' }),
  createVersion: (id: string, req: { content?: string; major?: boolean; comment?: string }) =>
    fetchAPI<{ id: string; major: number; minor: number }>(`${BASE}/api/v1/documents/${id}/versions`, { method: 'POST', body: JSON.stringify(req) }),
  runOCR: (id: string, template?: string) =>
    fetchAPI<OCRResult>(`${BASE}/api/v1/documents/${id}/ocr`, { method: 'POST', body: JSON.stringify({ template }) }),
  runExtraction: (id: string) =>
    fetchAPI<AIExtraction>(`${BASE}/api/v1/documents/${id}/extract`, { method: 'POST', body: '{}' }),
  signDocument: (id: string, signers: { name: string; email: string }[]) =>
    fetchAPI<{ signatures: SignatureRecord[] }>(`${BASE}/api/v1/documents/${id}/sign`, { method: 'POST', body: JSON.stringify({ signers }) }),
  search: (req: SearchRequest) => {
    const params = new URLSearchParams();
    if (req.q) params.set('q', req.q);
    if (req.category) params.set('category', req.category);
    if (req.folder_id) params.set('folder_id', req.folder_id);
    if (req.module) params.set('module', req.module);
    if (req.semantic) params.set('semantic', 'true');
    if (req.limit) params.set('limit', String(req.limit));
    return fetchAPI<{ documents: Document[] }>(`${BASE}/api/v1/documents/search?${params}`);
  },
  listFolders: () => fetchAPI<{ folders: Folder[] }>(`${BASE}/api/v1/documents/folders`),
};
